// Reusable entity-attachment panel (Drive Phase 2).
//
// Drop into any entity detail page: <AttachmentsPanel component="purchase_order"
// objectId={po.id} />. Entity-type-agnostic — one mechanism, many surfaces
// (order / shipment / seller_org / service_provider / purchase_order). Talks to
// the USE_REAL_API-gated api/attachments.ts (real /drive/* in prod, MSW sim in
// dev). The owning org is resolved server-side from the entity.

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PexIcon } from './icons/PexIcon'
import {
  deleteAttachment,
  fetchAttachments,
  uploadAttachment,
  type AttachmentRow,
} from '../api/attachments'
import { formatDateTime } from '../lib/datetime'
import { useT } from '../i18n'
import { attachmentsI18n } from '../i18n/shared/attachments'
import { FileLifecycleControls } from './FileLifecycleControls'
import { Button } from './ui/button'

type Props = {
  component: string
  objectId: string
  title?: string
  /** Default role stamped on uploads (the generic surface uses 'document'). */
  role?: string
  /**
   * Show only rows whose `role` matches. Two panels on the same
   * (component, objectId) — e.g. a work order's design files vs. its
   * proof-of-work photos — otherwise each see the other's uploads too, since
   * the underlying store has no notion of "which panel" an upload came from.
   */
  filterRole?: string
  className?: string
  /** Render per-file Phase-3 controls (approve/lock + public-link QR). */
  lifecycle?: boolean
}

function fmtSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AttachmentsPanel({
  component,
  objectId,
  title,
  role = 'document',
  filterRole,
  className = '',
  lifecycle = false,
}: Props) {
  const t = useT(attachmentsI18n)
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const queryKey = ['drive-attachments', component, objectId]

  const { data, isLoading } = useQuery<AttachmentRow[]>({
    queryKey,
    queryFn: () => fetchAttachments(component, objectId),
  })

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadAttachment(component, objectId, f, role)
    },
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey })
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.uploadFailed),
  })

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => deleteAttachment(component, objectId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  })

  const rows = filterRole ? (data ?? []).filter((a) => a.role === filterRole) : (data ?? [])
  const busy = uploadMutation.isPending

  return (
    <section
      className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] shadow-sm ${className}`}
    >
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
        <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
          <PexIcon name="attachment" className="h-[14px] w-[14px]" size="xs" tone="inherit" />
          {title ?? t.title}
          {rows.length > 0 && (
            <span className="rounded-full bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-muted)]">
              {rows.length}
            </span>
          )}
        </h2>
        <Button
          type="button"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="gap-1.5"
        >
          {busy ? (
            <PexIcon name="loading" className="h-[12px] w-[12px] animate-spin" size="xs" tone="inherit" />
          ) : (
            <PexIcon name="upload" className="h-[12px] w-[12px]" size="xs" tone="inherit" />
          )}
          {busy ? t.uploading : t.upload}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            if (files.length > 0) uploadMutation.mutate(files)
          }}
        />
      </div>

      <div className="p-5">
        {error && (
          <p
            role="alert"
            className="mb-3 rounded-[var(--radius-control)] border border-[color-mix(in_srgb,var(--color-danger)_24%,transparent)] bg-[color-mix(in_srgb,var(--color-danger)_8%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-danger)]"
          >
            {error}
          </p>
        )}
        {isLoading ? (
          <p className="text-xs italic text-[var(--color-text-muted)]">{t.loading}</p>
        ) : rows.length === 0 ? (
          <p className="text-xs italic text-[var(--color-text-muted)]">
            {t.empty}
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((a) => (
              <li
                key={a.id}
                className="rounded border border-[var(--color-border)] p-2 hover:bg-[var(--color-surface-muted)]"
              >
                <div className="flex items-center gap-3">
                {a.thumbnail_url ? (
                  <img
                    src={a.thumbnail_url}
                    alt={a.original_filename}
                    className="h-12 w-12 flex-shrink-0 rounded border border-[var(--color-border)] object-cover"
                  />
                ) : (
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
                    <PexIcon name="documents" size="sm" tone="inherit" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  {a.download_url ? (
                    <a
                      href={a.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-xs font-medium text-[var(--color-text)] decoration-[var(--color-signal)] underline-offset-4 hover:underline"
                    >
                      {a.original_filename}
                    </a>
                  ) : (
                    <span className="truncate text-xs font-medium text-[var(--color-text)]">
                      {a.original_filename}
                    </span>
                  )}
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--color-text-muted)]">
                    <span className="rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 font-medium uppercase tracking-wide">
                      {a.role}
                    </span>
                    {a.file_size > 0 && <span>{fmtSize(a.file_size)}</span>}
                    <span>·</span>
                    <span>
                      {formatDateTime(a.uploaded_at, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteMutation.mutate(a.id)}
                  disabled={deleteMutation.isPending}
                  className="shrink-0 text-[var(--color-danger)] hover:bg-[color-mix(in_srgb,var(--color-danger)_8%,transparent)] hover:text-[var(--color-danger)]"
                  title={t.delete}
                  aria-label={t.delete}
                >
                  <PexIcon name="trash" className="h-[12px] w-[12px]" size="xs" tone="inherit" />
                </Button>
                </div>
                {lifecycle && (
                  <FileLifecycleControls file={a} invalidateKey={queryKey} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
